/**
 * The review panel of one page (#376): what was read, line by line, and the
 * learner's confirmation of it.
 *
 * "Present what you grade": nothing is graded from this page until the learner
 * has confirmed it, and what they confirm is the exact set of lines they were
 * looking at. So **Confirm page sends the `regionsDigest` of the response that
 * is on screen** - captured from the render the button was clicked in, never
 * from a refetch made on the way. If the page changed in between, the server
 * answers `transcription_changed`, records nothing, and this panel reloads the
 * page and says so, so the learner checks again instead of confirming text they
 * did not see.
 *
 * A text-identical pair of lines that split is asked about once, as one card
 * (`region-list.tsx`, #392); a line the learner says is not on their page is
 * left out of what they confirm.
 *
 * Counts of lines are fine; no percentage, bar or fraction appears here.
 */

import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/client.js";
import { queryKeys } from "../../api/query-keys.js";
import { useApi } from "../../app/api-context.js";
import { FailureNotice } from "../../app/failure-notice.js";
import { useHomeworkLabels } from "../../i18n/homework-labels.js";
import { GuardedButton } from "./answer-lock.js";
import { CheckRow, type CheckStep } from "./check-row.js";
import { POLL_INTERVAL_MS } from "./polling.js";
import { ReaderFailures } from "./reader-failures.js";
import { RegionList } from "./region-list.js";

export interface PageReviewProps {
  readonly pageId: string;
  /** 1-based, for the heading. */
  readonly pageNumber: number;
  /** A "Read again" was just asked for (#395): keep asking the server for this page until the window closes. */
  readonly readingAgain?: boolean;
  /**
   * Where the page's one action leads (#641). With it, the end-of-page button confirms AND goes there (same call, same
   * rule), and a page already confirmed shows only a forward link. Without it, the plain "Confirm page" button.
   */
  /** The row's "Page k" (#648); the page's own number when absent. */
  readonly pageLabel?: string;
  /** Where "Back to the summary" leads. Absent: no such link. */
  readonly summaryTo?: string;
  readonly forward?: {
    readonly to: string;
    readonly confirmLabel: string;
    readonly forwardLabel: string;
  };
}

export function PageReview({
  pageId,
  pageNumber,
  readingAgain = false,
  forward,
  pageLabel,
  summaryTo,
}: PageReviewProps): React.JSX.Element {
  const t = useHomeworkLabels();
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const headingId = `homework-page-${pageId}`;

  const transcription = useQuery({
    queryKey: queryKeys.homeworkTranscription(pageId),
    queryFn: () => api.getHomeworkTranscription(pageId),
    // Until both readers have been heard from there is nothing to review yet - and after a "Read again" the page may change.
    refetchInterval: (query) =>
      query.state.data?.readingComplete === false || readingAgain
        ? POLL_INTERVAL_MS
        : false,
  });

  const confirm = useMutation({
    // The digest is an argument, taken from the render the click happened in.
    mutationFn: (regionsDigest: string) =>
      api.confirmHomeworkPage(pageId, crypto.randomUUID(), { regionsDigest }),
    onSuccess: (updated) => {
      queryClient.setQueryData(
        queryKeys.homeworkTranscription(pageId),
        updated,
      );
    },
    // Success or refusal, what is on screen may no longer be what the server
    // holds (`transcription_changed` is exactly that), so read it again.
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.homeworkTranscription(pageId),
      });
      if (transcription.data !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.homeworkUpload(transcription.data.uploadId),
        });
      }
    },
  });

  // The lines that are showing a text box the learner could still save. While
  // any is, the page cannot be confirmed: confirming would accept, unchanged,
  // the very line the learner has just said is not what they wrote.
  const [editingRegions, setEditingRegions] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const handleEditingChange = useCallback(
    (regionId: string, editing: boolean) => {
      setEditingRegions((current) => {
        if (current.has(regionId) === editing) return current;
        const next = new Set(current);
        if (editing) next.add(regionId);
        else next.delete(regionId);
        return next;
      });
    },
    [],
  );

  // The full-page error is for a page that has never loaded. A background read
  // that fails AFTER data arrived keeps that data - and every open box and
  // everything typed in it - on screen: unmounting the lines would throw the
  // learner's text away and re-enable Confirm.
  const data = transcription.data;
  const row = (step?: CheckStep, onGlance = false): React.JSX.Element => (
    <CheckRow
      page={pageLabel ?? t.pageN(pageNumber)}
      pageTo={summaryTo}
      step={step}
      needing={
        onGlance
          ? undefined
          : data?.regions.filter((region) => !region.resolved).length
      }
    />
  );
  const summaryLink =
    summaryTo === undefined ? null : (
      <Link to={summaryTo}>{t.backToSummary}</Link>
    );
  if (data === undefined) {
    if (transcription.isError) {
      return (
        <section aria-labelledby={headingId}>
          <h2 id={headingId} className="visually-hidden">
            {t.pageN(pageNumber)}
          </h2>
          {row()}
          <FailureNotice
            error={transcription.error}
            onRetry={() => void transcription.refetch()}
          />
          {summaryLink === null ? null : (
            <p className="check-quiet">{summaryLink}</p>
          )}
        </section>
      );
    }
    return (
      <section aria-labelledby={headingId}>
        <h2 id={headingId} className="visually-hidden">
          {t.pageN(pageNumber)}
        </h2>
        {row()}
        <p role="status">{t.loadingPage}</p>
        {summaryLink === null ? null : (
          <p className="check-quiet">{summaryLink}</p>
        )}
      </section>
    );
  }

  // What is on screen may be out of date; confirming it would confirm a page
  // the learner can no longer be sure they are looking at.
  const readFailed = transcription.isError;
  // #395: both readers failed - nothing was read from this page, so there is nothing to check or confirm.
  const unreadable =
    data.readingComplete &&
    data.readers.length > 0 &&
    data.readers.every((reader) => reader.outcome === "failed");
  const unsavedChange = editingRegions.size > 0;
  const canConfirm =
    data.allResolved && !data.confirmed && !unsavedChange && !readFailed;
  const showsList =
    data.readingComplete && !unreadable && data.regions.length > 0;
  const confirmSentence =
    confirm.error instanceof ApiRequestError
      ? t.reviewFailureSentence(confirm.error.code) || undefined
      : undefined;

  // A refused confirmation is said in the confirm card, next to the button, which stays pressable for a retry (it is
  // disabled only while the call is pending). When the page changed under the learner it also changes how many steps
  // there are, and they are rarely still on the last one: the card is not drawn then, so the same notice stands where it
  // would have been.
  const confirmFailure = confirm.isError ? (
    confirmSentence !== undefined ? (
      <div className="notice notice-error" role="alert">
        <p>{confirmSentence}</p>
      </div>
    ) : (
      <FailureNotice error={confirm.error} />
    )
  ) : null;

  // Drawn after the last line when the lines are stepped through (#619), and as before when they are all shown.
  // Its button is the same button, with the same enabling rule, wherever it is drawn.
  const confirmPanel = (
    <div className="panel">
      <h3>{t.confirmHeading}</h3>
      {data.confirmed ? (
        <p role="status">{t.confirmedStatus}</p>
      ) : (
        <p className="field-hint">{t.confirmingSays}</p>
      )}
      {data.confirmed && forward !== undefined ? (
        <Link className="hw-primary" to={forward.to}>
          {forward.forwardLabel}
        </Link>
      ) : (
        <GuardedButton
          disabled={!canConfirm || confirm.isPending}
          onActivate={() => {
            confirm.reset();
            // Navigate only once the server has recorded the confirmation; a refusal stays here and is said here.
            confirm.mutate(data.regionsDigest, {
              onSuccess: () => {
                if (forward !== undefined) void navigate(forward.to);
              },
            });
          }}
        >
          {forward?.confirmLabel ?? t.confirmPage}
        </GuardedButton>
      )}
      {confirmFailure}
      {readFailed && !data.confirmed ? (
        <p className="field-hint">{t.confirmPaused}</p>
      ) : null}
      {unsavedChange && !data.confirmed ? (
        <p className="field-hint" role="status">
          {t.saveOrCancelFirst}
        </p>
      ) : null}
    </div>
  );

  return (
    <section aria-labelledby={headingId}>
      <h2 id={headingId} className="visually-hidden">
        {t.pageN(pageNumber)}
      </h2>
      {showsList ? null : row()}

      {readFailed ? (
        <FailureNotice
          error={transcription.error}
          onRetry={() => void transcription.refetch()}
        />
      ) : null}

      <ReaderFailures readers={data.readers} />

      {!data.readingComplete ? (
        <p role="status">{t.stillBeingRead}</p>
      ) : unreadable ? (
        <p role="status">{t.couldNotReadPage}</p>
      ) : (
        <>
          {data.regions.length === 0 ? (
            <p>{t.noLinesRead}</p>
          ) : (
            <>
              <RegionList
                regions={data.regions}
                pageId={pageId}
                uploadId={data.uploadId}
                pageSize={{ widthPx: data.widthPx, heightPx: data.heightPx }}
                onEditingChange={handleEditingChange}
                after={(atEnd) =>
                  atEnd || data.confirmed ? confirmPanel : confirmFailure
                }
                row={row}
                quietEnd={summaryLink}
              />
            </>
          )}

          {data.regions.length === 0 ? confirmPanel : null}
        </>
      )}
      {showsList || summaryLink === null ? null : (
        <p className="check-quiet">{summaryLink}</p>
      )}
    </section>
  );
}
